// A parte GEO do "Salvar" da ficha do cliente no painel (`ClientFormModal`, edição pelo admin). Módulo PURO, sem import
// do Firebase (roda no jest do CRA): o modal relê o doc numa transação, pede o plano aqui e troca `apagar` por
// `deleteField()`.
//
// ⚠️ POR QUE EXISTE (rodada 5 da revisão do GPS do cliente, 29/10/2026 — V2 da revisão de 28/10). O Salvar gravava SEMPRE
// a `location` e o mapa `address` INTEIRO da foto de quando a ficha ABRIU. Com a ficha aberta enquanto a sincronização
// plantava um ERP novo, trazia um endereço novo do Sankhya ou o vendedor gravava 'gps' no check-in, o Salvar — mesmo só
// do telefone — devolvia o ponto e o endereço VELHOS; e os hashes da sync já tinham sido consumidos, então a mudança
// se perdia de vez (a sync seguinte não a traria de novo). Agora:
//   • vai só o que o admin MUDOU em relação ao que a ficha CARREGOU: a `location` só com a coordenada mudada (a régua
//     numérica de sempre) e cada campo de endereço por dot-path (`address.street`...), comparado como o formulário
//     grava (trim; UF maiúscula; complemento '' ≡ null);
//   • CONFLITO: o admin mudou algo geo (a coordenada ou um campo de endereço) e QUALQUER parte geo do doc RELIDO (a
//     coordenada, a precisão ou um campo de endereço) não é mais a que a ficha carregou → nada é gravado, e a ficha pede
//     para reabrir. Coordenada e endereço são UM bloco: a coordenada digitada vendo o endereço antigo, enquanto a sync
//     trocava o endereço, ficaria sem carimbo e valeria para o endereço NOVO (a revisão da rodada 5 apontou);
//   • a guarda do 'gps' olha a precisão do doc RELIDO (o check-in pode ter chegado com a ficha aberta);
//   • o CARIMBO do ponto (o mesmo da sincronização — `carimboEnderecoDoPonto`, functions/geoServidor.js), na chave do
//     PAINEL (`geoAudit.carimboPainel = { ponto, endereco }` — rodada 6: o carimbo principal, `geoAudit.ponto` +
//     `enderecoDoPonto`, é só da regra): (a) quando só o endereço muda e o ponto ainda não tem origem de fonte nenhuma, ele
//     ganha o endereço para o qual vale (o de antes, relido); (b) a coordenada DIGITADA ganha o endereço que fica depois
//     deste Salvar — senão uma sincronização que leu o índice ANTES do Salvar e troca o endereço DEPOIS deixava a digitada
//     sem carimbo, e ela valeria para o endereço novo (A1 da revisão da rodada 5). A regra do servidor compara esse
//     endereço com o atual e não aprova o ponto contra um endereço que não é o dele (functions/cnefe.js, "O ENDEREÇO DO
//     PONTO", e `origemDoPonto`);
//   • LIMPAR a coordenada que veio do Sankhya (o doc relido 'erp' ou com `geoAudit.erp`) é RECUSADO (rodada 6 — A5): o
//     `location: null` apagava a âncora do carimbo, e o ponto seguinte (o ERP guardado que a divergência tinha rejeitado,
//     ou a LAT/LNG inalterada que a sync replanta) era julgado como 1ª avaliação contra o endereço atual e navegava. A
//     política é corrigir no Sankhya — ou digitar a coordenada certa aqui.
// As marcas são as de antes (`geoCity: null`; `geoNavegavel: false` quando só o endereço mudou e apagado quando a
// coordenada mudou; a pendência sai; `geoDivergTentativas: 0`; `geoPrecision: 'manual'` na coordenada digitada) — mas só
// quando um campo geo mudou: o Salvar só do telefone não manda nada daqui.

/** Ponto como vem do Firestore (ou de onde for): conferido aqui, nunca confiado. */
export interface PontoGeo {
  latitude?: unknown;
  longitude?: unknown;
}
/** Endereço do cliente (os campos podem vir em qualquer tipo; o número às vezes é gravado como número). */
export interface EnderecoGeo {
  street?: unknown;
  number?: unknown;
  complement?: unknown;
  neighborhood?: unknown;
  city?: unknown;
  state?: unknown;
  zipCode?: unknown;
}
/** O que o plano lê do cliente: o que a ficha carregou (`inicial`) ou o doc relido na transação (`atual`). */
export interface DocGeo {
  location?: PontoGeo | null;
  address?: EnderecoGeo | null;
  geoPrecision?: string | null;
  geoPendencia?: unknown;
  geoPendente?: unknown;
  /** só `ponto`, `carimboPainel`, `carimboSync` (a origem do ponto) e `erp` (a coordenada que veio do Sankhya)
   *  interessam; `unknown` porque o `GeoAudit` dos tipos do painel não os declara (os tipos do app e do painel são
   *  espelhados, e o app não muda nesta rodada). */
  geoAudit?: unknown;
}
type CarimboEscritor = { ponto?: PontoGeo | null; endereco?: unknown };
type CarimboAudit = { ponto?: PontoGeo | null; carimboPainel?: unknown; carimboSync?: unknown; erp?: unknown };
/** Os campos do formulário que entram no plano (texto, como o `<input>` devolve). */
export interface FormGeo {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: string;
  longitude: string;
}
/** `campos` vai no `update` (chaves com dot-path); `apagar` = caminhos que o modal troca por `deleteField()`;
 *  `conflito` = a frase do `setError` (nada é gravado). */
export interface PlanoGeo {
  campos: Record<string, unknown>;
  apagar: string[];
  conflito: string | null;
}

export const CAMPOS_ENDERECO = ['street', 'number', 'complement', 'neighborhood', 'city', 'state', 'zipCode'] as const;
export type CampoEndereco = (typeof CAMPOS_ENDERECO)[number];

export const MSG_CONFLITO_GEO =
  'O endereço ou a coordenada deste cliente mudou enquanto a ficha estava aberta. Feche e abra de novo para não desfazer a alteração.';
/** A recusa de LIMPAR a coordenada que veio do Sankhya (rodada 6 — A5); vai no mesmo `conflito` (o `setError` de sempre). */
export const MSG_LIMPAR_ERP =
  'A coordenada deste cliente veio do Sankhya e não pode ser apagada aqui. Corrija a latitude/longitude no Sankhya ou digite a coordenada certa nesta ficha.';

/**
 * Cópia TS de `copiaEndereco` (functions/cnefe.js — a de lá é a fonte): os 7 campos de `CAMPOS_ENDERECO`, cada um como
 * TEXTO (null/ausente → ''). É o `geoAudit.enderecoDoPonto` que a regra do servidor lê; o teste deste módulo confere
 * as duas lado a lado.
 */
export function copiaEndereco(address: EnderecoGeo | null | undefined): Record<CampoEndereco, string> {
  const a: EnderecoGeo = address && typeof address === 'object' ? address : {};
  const out = {} as Record<CampoEndereco, string>;
  for (const k of CAMPOS_ENDERECO) out[k] = a[k] == null ? '' : String(a[k]);
  return out;
}

const texto = (v: unknown) => String(v ?? '').trim();
/** O valor como o formulário GRAVA o campo (o mesmo `trim`/maiúscula/null de sempre do `ClientFormModal`). */
function comoGrava(k: CampoEndereco, v: unknown): string | null {
  const s = texto(v);
  if (k === 'state') return s.toUpperCase();
  if (k === 'complement') return s || null;
  return s;
}
/** Comparável: o que seria gravado, com o complemento '' ≡ null e o CEP pelos DÍGITOS (rodada 6 — A6, como a rodada 4
 *  fazia: reescrever só o formato, "01000-000" × "01000000", não é mudança — não marca e não é conflito). */
const comparavel = (k: CampoEndereco, v: unknown) => (k === 'zipCode' ? texto(v).replace(/\D/g, '') : comoGrava(k, v) ?? '');

/** {lat, lng} utilizável — a régua do servidor (`pontoDe`, functions/cnefe.js): números finitos e não (0,0). */
function pontoUtil(l: PontoGeo | null | undefined): { lat: number; lng: number } | null {
  const lat = l?.latitude, lng = l?.longitude;
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return lat === 0 && lng === 0 ? null : { lat, lng };
}
/** Mesma coordenada nos dois docs (exata; ausente só bate com ausente). */
function mesmaCoordenada(a: PontoGeo | null | undefined, b: PontoGeo | null | undefined): boolean {
  return (a?.latitude ?? null) === (b?.latitude ?? null) && (a?.longitude ?? null) === (b?.longitude ?? null);
}
const ehObjeto = (e: unknown): e is Record<string, unknown> => !!e && typeof e === 'object' && !Array.isArray(e);
/** Cópia de `origemDoPonto` (functions/cnefe.js), só o sim/não "o ponto atual já tem origem de alguma fonte": o carimbo
 *  principal da regra (`geoAudit.ponto` é exatamente a location — com `enderecoDoPonto` ou "decidido sem origem", que a
 *  regra resolve sem olhar carimbo de escritor), ou o carimbo do painel ou da sync para ESTE ponto. Com origem, o painel
 *  não carimba: a que está lá é a de verdade (a 2ª mudança antes da rotina não a troca). */
function temOrigem(d: DocGeo): boolean {
  const aud = ehObjeto(d.geoAudit) ? (d.geoAudit as CarimboAudit) : null;
  const p = pontoUtil(d.location);
  if (!aud || !p) return false;
  const mesmo = (l: PontoGeo | null | undefined) => { const q = pontoUtil(l); return !!q && q.lat === p.lat && q.lng === p.lng; };
  if (mesmo(aud.ponto)) return true;
  return [aud.carimboPainel, aud.carimboSync].some((c) => ehObjeto(c) && mesmo((c as CarimboEscritor).ponto) && ehObjeto((c as CarimboEscritor).endereco));
}
/** A coordenada do doc veio do Sankhya: 'erp', ou o ERP guardado pela regra (`geoAudit.erp`: o 'cnefe' da divergência, o
 *  'aprox'/'city' do ERP a mais de 50 km). */
function veioDoSankhya(d: DocGeo): boolean {
  const aud = ehObjeto(d.geoAudit) ? (d.geoAudit as CarimboAudit) : null;
  return d.geoPrecision === 'erp' || (!!aud && aud.erp != null);
}
/** Porte do `complementoNoTextoDoApp` do servidor (functions/geoServidor.js): o complemento tem o "KM" que o app põe no
 *  texto do Maps (cru, ou na normalização do `norm` do cnefe.js — sem acento e sem pontuação). Largo de propósito: só
 *  marca para reavaliar; quem decide se o KM mudou é a regra do servidor. */
function kmNoTexto(v: unknown): boolean {
  const s = String(v ?? '');
  const n = s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  return /\bKM/i.test(s) || /\bKM/.test(n);
}

/**
 * O plano da parte geo do Salvar. `inicial` = o cliente como a ficha o CARREGOU (o `client` do modal); `form` = os campos
 * digitados; `atual` = o doc relido na transação.
 */
export function planoGeoDaEdicao({ inicial, form, atual }: { inicial: DocGeo; form: FormGeo; atual: DocGeo }): PlanoGeo {
  const nada: PlanoGeo = { campos: {}, apagar: [], conflito: null };
  // A régua numérica de sempre: o form vem do `toString()` da coordenada carregada, e o `parseFloat` devolve o mesmo
  // double — sem mexer, não conta. Redigitar o MESMO valor também não conta (custo aceito, documentado no item 30).
  const lat = parseFloat(String(form.latitude ?? '')), lng = parseFloat(String(form.longitude ?? ''));
  const nova = !isNaN(lat) && !isNaN(lng) ? { latitude: lat, longitude: lng } : null;
  const coordMudou = (inicial.location?.latitude ?? null) !== (nova ? nova.latitude : null)
    || (inicial.location?.longitude ?? null) !== (nova ? nova.longitude : null);
  const a0: EnderecoGeo = inicial.address || {};
  const mudados = CAMPOS_ENDERECO.filter((k) => comparavel(k, form[k]) !== comparavel(k, a0[k]));
  if (!coordMudou && !mudados.length) return nada;

  // Conflito: o admin mexeu no bloco geo e o bloco mudou por fora desde que a ficha abriu → gravar desfaria a outra
  // mudança (a do Sankhya, a do 'gps', a da rotina) ou juntaria uma coordenada com um endereço que não é o dela. O bloco
  // inteiro, e não só o campo mexido: coordenada e endereço só valem juntos (o carimbo do ponto depende disso).
  const a1: EnderecoGeo = atual.address || {};
  if (!mesmaCoordenada(inicial.location, atual.location) || (inicial.geoPrecision ?? null) !== (atual.geoPrecision ?? null)
    || CAMPOS_ENDERECO.some((k) => comparavel(k, a1[k]) !== comparavel(k, a0[k]))) return { ...nada, conflito: MSG_CONFLITO_GEO };

  const campos: Record<string, unknown> = {};
  const apagar: string[] = [];
  for (const k of mudados) campos[`address.${k}`] = comoGrava(k, form[k]);
  // Endereço que a regra do servidor lê (rua, número, bairro, cidade, UF, CEP) — e o complemento quando um dos lados tem
  // o KM que o app põe no texto do Maps (rodada 5: o KM mudado não marcava, e o `true` do ponto do KM antigo ficava).
  const enderecoMudou = mudados.some((k) => k !== 'complement' || kmNoTexto(form.complement) || kmNoTexto(a0.complement));
  // A pendência de endereço era sobre o cadastro ANTIGO — sai, como na sincronização (`camposReavaliar`); a rotina põe de
  // volta se a correção não bastou. Só o que EXISTE no doc relido é apagado.
  const tirarPendencia = () => {
    if (atual.geoPendencia != null) apagar.push('geoPendencia');
    if (atual.geoPendente === true) campos.geoPendente = false;
  };

  // A coordenada confirmada em campo ('gps', pelo doc RELIDO) é verdade: não é apagada nem re-geocodificada por uma edição
  // de cadastro. Só uma coordenada digitada DIFERENTE a troca (cai no ramo de baixo, como sempre).
  if (atual.geoPrecision === 'gps' && (!coordMudou || nova === null)) {
    if (enderecoMudou) tirarPendencia(); // o 'gps' não depende do endereço: só a pendência sai
    return { campos, apagar, conflito: null };
  }
  // LIMPAR a coordenada que veio do Sankhya: recusado, nada é gravado (rodada 6 — A5; ver o cabeçalho). Vale também para a
  // coordenada digitada que o servidor lê como VAZIA — 0/0, não finita (rodada 7, 2º achado alto da revisão da rodada 6:
  // "digite a coordenada certa" levava a 0/0, que passava como 'manual' e tinha o mesmo efeito do limpar). A régua do
  // `coordMudou` fica a mesma: o doc que já tem {0,0} salvo sem mexer na coordenada não conta como mudança. Limpar a
  // 'manual', o 'cnefe' sem ERP guardado, o 'street'/'postal' continua como sempre — o perigo de a sync replantar a
  // LAT/LNG do Sankhya de um endereço antigo é fechado no servidor (`sankhya.enderecoDaCoord`, functions/geoServidor.js).
  if (coordMudou && !pontoUtil(nova) && veioDoSankhya(atual)) return { campos: {}, apagar: [], conflito: MSG_LIMPAR_ERP };
  if (!coordMudou && !enderecoMudou) return { campos, apagar, conflito: null }; // só o complemento sem KM: não marca

  campos.geoCity = null; // a rotina do servidor reavalia (endereço ou coordenada mudou)
  // (O `geoDivergTentativas` era o teto da rotina antiga do Nominatim, aposentada em 27/10/2026: ninguém mais o lê;
  // zerado para não ficar um valor velho para trás.)
  campos.geoDivergTentativas = 0;
  if (coordMudou) {
    // A decisão do servidor sobre o "Navegar" era sobre a coordenada ANTIGA: sai (um `true` levaria o vendedor ao ponto
    // novo sem ninguém ter olhado; um `false` ignoraria a correção). Coordenada DIGITADA ganha 'manual' (o app só a usa
    // com a cidade confirmada pelo servidor); LIMPAR não ganha precisão (e sem coordenada não há o que carimbar).
    campos.location = nova;
    apagar.push('geoNavegavel');
    if (nova) {
      campos.geoPrecision = 'manual';
      // (b) o CARIMBO da digitada: vale para o endereço que fica DEPOIS deste Salvar (o relido + o que o admin mudou) — a
      // sincronização que leu o índice antes do Salvar e troca o endereço depois não a aprova contra o endereço novo.
      const depois: EnderecoGeo = { ...(atual.address || {}) };
      for (const k of mudados) depois[k] = comoGrava(k, form[k]);
      campos['geoAudit.carimboPainel'] = { ponto: { latitude: nova.latitude, longitude: nova.longitude }, endereco: copiaEndereco(depois) };
    }
  } else {
    // Só o ENDEREÇO mudou: `false`, e não apagar (revisão de 28/10/2026) — com `geoCity: null` o APK 1.7.x aceitaria um
    // `true` velho por leniência e mandaria o vendedor ao ponto julgado para o endereço ANTIGO até a rotina decidir.
    campos.geoNavegavel = false;
    // (a) o CARIMBO: o ponto que fica vale para o endereço de ANTES (o relido) — se ainda não tem origem de fonte nenhuma.
    const p = pontoUtil(atual.location);
    if (p && !temOrigem(atual)) campos['geoAudit.carimboPainel'] = { ponto: { latitude: p.lat, longitude: p.lng }, endereco: copiaEndereco(atual.address) };
  }
  tirarPendencia();
  return { campos, apagar, conflito: null };
}
